/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import { __, humanizeTime } from '@oat-sa-private/ui-core';
import { getItemByIdentifier } from '../util/testMap.js';
import { getPartTitle } from '../util/testPart.js';

function getTestPart(testMap, timer, testContext) {
    const testPartId = timer.id || testContext?.testPartId;
    return testMap?.parts?.[testPartId] || null;
}

function getSection(testMap, timer, testContext) {
    const sectionId = timer.id || testContext?.sectionId;
    for (const testPart of Object.values(testMap?.parts || {})) {
        if (sectionId && testPart.sections?.[sectionId]) {
            return testPart.sections[sectionId];
        }
    }

    return null;
}

function getItem(testMap, timer, testContext) {
    const itemIdentifier = timer.id || testContext?.itemIdentifier;
    return itemIdentifier ? getItemByIdentifier(testMap || {}, itemIdentifier) : null;
}

function getTimerTitle(timer, testMap, testContext) {
    if (!timer || !timer.level) {
        return null;
    }

    if (timer.level === 'testPart') {
        const testPart = getTestPart(testMap, timer, testContext);
        return testPart ? getPartTitle(testPart, testMap, true) || getPartTitle(testPart, testMap) : null;
    }

    if (timer.level === 'section') {
        return getSection(testMap, timer, testContext)?.label || null;
    }

    if (timer.level === 'item') {
        return getItem(testMap, timer, testContext)?.label || null;
    }

    return null;
}

function getTestPartWarningMessage(readableTime, title) {
    const partLabel = __('Part');

    if (!title) {
        return __('You have %s left to finish this part', readableTime);
    }

    if (title.startsWith(`${partLabel} `)) {
        return __('You have %s left to finish %s', readableTime, title);
    }

    return __('You have %s left to finish %s part', readableTime, title);
}

/**
 * Resolve the visible timer warning notification message for the active context.
 * @param {Object} timer
 * @param {String} timer.level
 * @param {String} [timer.id]
 * @param {Number} thresholdMs
 * @param {Object} testMap
 * @param {Object} testContext
 * @returns {String}
 */
export function getTimerWarningMessage(timer, thresholdMs, testMap, testContext) {
    const readableTime = humanizeTime(thresholdMs / 1000);
    const title = getTimerTitle(timer, testMap, testContext);

    switch (timer?.level) {
        case 'test':
            return __('You have %s left to finish the test', readableTime);

        case 'testPart':
            return getTestPartWarningMessage(readableTime, title);

        case 'section':
            return title
                ? __('You have %s left to finish %s section', readableTime, title)
                : __('You have %s left to finish this section', readableTime);

        case 'item':
            return title
                ? __('You have %s left to finish %s item', readableTime, title)
                : __('You have %s left to finish this item', readableTime);

        default:
            return __('You have %s left to finish the test', readableTime);
    }
}
